<template>
  <themed-modal
    name="document-upload-modal"
    title="Upload Documents"
    size="md"
    :click-to-close="!isUploading"
    @opened="handleOpened"
    @closed="handleClosed"
  >
    <form @submit.prevent="handleSubmit" class="document-upload-form">
      <!-- File Input -->
      <div class="form-group">
        <label for="document-files" class="form-label required">
          Select Files
        </label>
        <div class="file-input-wrapper">
          <input
            id="document-files"
            ref="fileInput"
            type="file"
            multiple
            @change="handleFileChange"
            class="file-input"
            :disabled="isUploading"
            aria-required="true"
            aria-describedby="file-input-help"
          />
          <div class="file-input-display">
            <theme-icon icon="upload" type="icons" classes="w-5 h-5" />
            <span v-if="selectedFiles.length === 0" class="file-input-placeholder">
              Click to select files or drag and drop
            </span>
            <span v-else class="file-input-selected">
              {{ selectedFiles.length }} file{{ selectedFiles.length !== 1 ? 's' : '' }} selected
            </span>
          </div>
        </div>
        <p id="file-input-help" class="form-help">
          You can select multiple files at once
        </p>
        <p v-if="errors.files" class="form-error" role="alert">
          {{ errors.files }}
        </p>
        
        <!-- Selected Files List -->
        <div v-if="selectedFiles.length > 0" class="selected-files-list">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="selected-file-item"
          >
            <theme-icon icon="file" type="icons" classes="w-4 h-4" />
            <span class="selected-file-name">{{ file.name }}</span>
            <span class="selected-file-size">{{ formatFileSize(file.size) }}</span>
            <button
              type="button"
              @click="removeFile(index)"
              class="selected-file-remove"
              :disabled="isUploading"
              aria-label="Remove file"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <themed-button
          type="button"
          variant="outline"
          @click="handleCancel"
          :disabled="isUploading"
        >
          Cancel
        </themed-button>
        <themed-button
          type="submit"
          variant="default"
          :disabled="isUploading || !canSubmit"
        >
          <svg
            v-if="isUploading"
            class="animate-spin w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isUploading ? 'Uploading...' : 'Upload' }}</span>
        </themed-button>
      </div>
    </form>
  </themed-modal>
</template>

<script>
export default {
  name: 'DocumentUploadModal',
  
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      selectedFiles: [],
      isUploading: false,
      errors: {},
      previouslyFocusedElement: null
    };
  },
  
  computed: {
    /**
     * Check if form can be submitted
     */
    canSubmit() {
      return this.selectedFiles.length > 0;
    }
  },
  
  methods: {
    /**
     * Handle modal opened event
     */
    handleOpened() {
      // Store the element that had focus before modal opened
      this.previouslyFocusedElement = document.activeElement;
      
      // Focus file input
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.focus();
        }
      });
      
      // Set up focus trap
      this.setupFocusTrap();
    },
    
    /**
     * Handle modal closed event
     */
    handleClosed() {
      // Reset form
      this.resetForm();
      
      // Restore focus to previously focused element
      if (this.previouslyFocusedElement) {
        this.$nextTick(() => {
          this.previouslyFocusedElement.focus();
        });
      }
      
      // Clean up focus trap
      this.removeFocusTrap();
    },
    
    /**
     * Handle file input change
     */
    handleFileChange(event) {
      const files = Array.from(event.target.files);
      this.selectedFiles = files;
      this.errors.files = '';
    },
    
    /**
     * Remove a file from selection
     */
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
      
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    /**
     * Format file size for display
     */
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    
    /**
     * Validate form
     */
    validateForm() {
      this.errors = {};
      
      if (this.selectedFiles.length === 0) {
        this.errors.files = 'Please select at least one file';
        return false;
      }
      
      return true;
    },
    
    /**
     * Handle form submission
     */
    async handleSubmit() {
      // Validate form
      if (!this.validateForm()) {
        return;
      }
      
      this.isUploading = true;
      
      try {
        // Build FormData
        const formData = new FormData();
        
        // Add files
        this.selectedFiles.forEach(file => {
          formData.append('file[]', file);
        });
        
        // Emit upload event to parent
        await this.$emit('upload', formData);
        
        // Close modal on success
        this.$modal.hide('document-upload-modal');
      } catch (error) {
        // Error is handled by parent component
        console.error('Upload error:', error);
      } finally {
        this.isUploading = false;
      }
    },
    
    /**
     * Handle cancel button
     */
    handleCancel() {
      this.$modal.hide('document-upload-modal');
    },
    
    /**
     * Reset form to initial state
     */
    resetForm() {
      this.selectedFiles = [];
      this.errors = {};
      this.isUploading = false;
      
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    /**
     * Set up focus trap for modal
     */
    setupFocusTrap() {
      this.handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          const modal = this.$el;
          if (!modal) return;
          
          const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };
      
      document.addEventListener('keydown', this.handleKeyDown);
    },
    
    /**
     * Remove focus trap
     */
    removeFocusTrap() {
      if (this.handleKeyDown) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }
};
</script>
